/**
 * TypeBox schema source-of-truth for the TESSA Lean settings surface.
 *
 * Story W6.S1 — lifts the settings JSON schema from
 * `packages/extension/package.json#contributes.configuration` into a typed
 * TypeBox object so:
 *  - The headless `@tessa-lean/server` (which has no
 *    `contributes.configuration` analogue) gets the same type validation.
 *  - The extension's `contributes.configuration` is regenerated from this
 *    schema by a build-time script (Task 3).
 *  - The web SPA settings panel (W6-S2) consumes `SettingsKey` and
 *    `SettingsType[K]` for type-safe form rendering.
 *
 * Constraints honoured:
 *  - **Package boundary (CLAUDE.md):** zero `vscode` imports — this file
 *    runs in the headless server and the extension equally. Conversion to
 *    the VS Code `contributes.configuration` JSON-schema dialect happens
 *    in `packages/extension/scripts/generate-package-json-config.mjs`
 *    (W6-S1 Task 3, follow-up).
 *  - **Result<T, E> discipline:** consumers of `MergedSettings.get()`
 *    receive plain values; validation failures are surfaced through
 *    `SettingsStore.loadOnce()`'s Result envelope, not via throws here.
 *  - **No `any`:** `Static<typeof Schema>` produces the `SettingsType`
 *    mapped object; secret-bearing keys are tagged via the `SettingsMeta`
 *    sidecar so the API redaction layer can pick them up without
 *    parsing the schema's `extends`/`$id` metadata.
 *
 * Coverage today (Story W6.S1, first pass):
 *  - Every `tessa.*` key already shipped in
 *    `packages/extension/package.json#contributes.configuration` as of
 *    branch `Context_Assistant`.
 *  - Net-new server-only keys called out in the W6-S1 spec
 *    (`tessa.client.mode`, `tessa.server.port`, `tessa.server.allowExternal`,
 *    `tessa.applier.batchWindowMs`, `tessa.applier.maxParallel`,
 *    `tessa.fileTree.includeGlobs`, `tessa.fileTree.usePolling`,
 *    `tessa.kbIndex.parallelism`, `tessa.syside.binPath`,
 *    `tessa.syside.poolSize`, `tessa.syside.idleReapMs`,
 *    `tessa.ui.theme`, `tessa.ui.utilityDrawer.autoOpenOnError`,
 *    `tessa.ui.showDebugTab`, `tessa.ui.banner.hideWhenHealthy`,
 *    `tessa.ui.banner.hideInEmbed`, `tessa.ui.statusBar`).
 *
 * Future-rebrand note: the W6 PRD §8 plan also calls for renaming the
 * top-level prefix from `tessa.*` to `tessa-lean.*`. That rename has been
 * deferred to a follow-up (the parent ticket explicitly notes
 * "Settings discovered later add to schema in follow-ups"). All keys
 * here therefore use the `tessa.*` prefix that the shipping extension
 * uses today; the rename will swap `SETTINGS_PREFIX` and the schema sub-
 * object property names in one atomic commit.
 *
 * **Known dual-prefix exception (W5-fix-pass MED 11.1):** the
 * W5-S2 thin-client mode flag is read by `extension.ts` and
 * `legacy-mode/mode-switch-controller.ts` as `tessa-lean.client.mode`
 * (matching `packages/extension/package.json:91-97`). It is intentionally
 * NOT mirrored under `tessa.client.mode` here — the W5 surface predates
 * this schema and migrating mid-cycle would invalidate user-stored values
 * during the deprecation window. When the W6 prefix-rename swap lands,
 * the new `SETTINGS_PREFIX = 'tessa-lean'` will naturally subsume the
 * client.mode key and the W5 reader sites will keep working unchanged.
 * Any new W6-S1 SettingsStore consumer that needs the client mode MUST
 * read it via `tessa-lean.client.mode` directly until the rename ships.
 *
 * **W5-fix-pass MED 11.2 — orphaned net-new keys.** The 9 server keys
 * declared below (`tessa.server.port`, `tessa.server.allowExternal`,
 * `tessa.applier.batchWindowMs/maxParallel`, `tessa.fileTree.*`,
 * `tessa.kbIndex.parallelism`, `tessa.syside.*`, `tessa.ui.*`) have
 * NO consumers in the extension or server today. They are reserved for
 * later W6 wiring; the schema-only declaration is intentional so the
 * SPA settings panel (W6-S2) can render the form scaffold even before
 * the back-end consumers come online. Removal is NOT in scope for the
 * W5 fix-pass.
 *
 * **MED-5 follow-up (post-merge polish, 2026-05-13).** The orphaned keys
 * are now tagged `@reservedForFuture` at each declaration site and
 * collected in {@link RESERVED_FOR_FUTURE_SETTINGS_KEYS} below. Schema
 * shape is unchanged — the keys still deserialize, get defaults, and
 * round-trip via the merged-settings API. The tag exists so the
 * schema-as-API-contract is honest: every entry in the constant has no
 * runtime consumer reading the merged-settings store as of the date
 * marked in each JSDoc. Track follow-up in
 * `_bmad-output/implementation-artifacts/post-merge-polish-followups.md`
 * (MED-5). When a consumer lands, remove the `@reservedForFuture` tag
 * and drop the key from the array — the test in `schema.test.ts` pins
 * the invariant.
 */
import { Type } from '@sinclair/typebox';
import { DEFAULT_VIEWS_OUTPUT_PATH } from './defaults.js';
/** Top-level prefix for every settings key. See module doc-block. */
export const SETTINGS_PREFIX = 'tessa';
const stringKey = (defaultValue, meta) => ({
    schema: Type.String({ default: defaultValue }),
    meta: { ...meta, default: defaultValue },
});
const numberKey = (defaultValue, meta) => ({
    schema: Type.Number({ default: defaultValue }),
    meta: { ...meta, default: defaultValue },
});
const booleanKey = (defaultValue, meta) => ({
    schema: Type.Boolean({ default: defaultValue }),
    meta: { ...meta, default: defaultValue },
});
const stringArrayKey = (defaultValue, meta) => ({
    schema: Type.Array(Type.String(), { default: [...defaultValue] }),
    meta: { ...meta, default: [...defaultValue] },
});
const recordOfNumberKey = (defaultValue, meta) => ({
    schema: Type.Record(Type.String(), Type.Number(), { default: { ...defaultValue } }),
    meta: { ...meta, default: { ...defaultValue } },
});
const enumKey = (values, defaultValue, meta) => ({
    schema: Type.Union(values.map((v) => Type.Literal(v)), { default: defaultValue }),
    meta: { ...meta, default: defaultValue, enum: values },
});
/**
 * Internal flat key table. Single source of truth — the schema and meta
 * sidecar both derive from this.
 *
 * Keys MUST be in `<dotted-path>` form (without the `tessa.` prefix —
 * the prefix is added by `qualifiedKey()` when serialising to file form).
 */
const KEY_TABLE = {
    // ───── Providers / Azure OpenAI ────────────────────────────────────────
    // NOTE: provider/credential keys are `workspace`-scoped (not `user`) so a
    // single admin-managed LLM configuration applies to everyone using the
    // workspace and is written to `<wsRoot>/.tessa-lean/settings.json`. The
    // settings route additionally gates provider-key WRITES behind an admin
    // principal, and the env layer (server boot) seeds these on first run.
    'azure.endpoint': stringKey('', {
        description: 'Azure OpenAI endpoint URL (e.g., https://myinstance.openai.azure.com).',
        scope: 'workspace',
        category: 'Providers / Azure OpenAI',
    }),
    'azure.apiKey': stringKey('', {
        description: 'Azure OpenAI API key.',
        scope: 'workspace',
        secret: true,
        category: 'Providers / Azure OpenAI',
    }),
    'azure.deploymentName': stringKey('gpt-5.5', {
        description: 'Azure OpenAI deployment name (model deployment).',
        scope: 'workspace',
        category: 'Providers / Azure OpenAI',
    }),
    'azure.apiVersion': stringKey('2025-04-01-preview', {
        description: 'Azure OpenAI API version.',
        scope: 'workspace',
        category: 'Providers / Azure OpenAI',
    }),
    'azure.additionalDeployments': stringArrayKey([], {
        description: 'Additional Azure OpenAI deployment names available in the model selector.',
        scope: 'workspace',
        category: 'Providers / Azure OpenAI',
    }),
    // ───── Provider selector ───────────────────────────────────────────────
    provider: enumKey(['azure-openai', 'bedrock'], 'azure-openai', {
        description: 'LLM provider to use: Azure OpenAI or AWS Bedrock (Claude).',
        scope: 'workspace',
        category: 'Providers',
    }),
    // ───── Providers / AWS Bedrock ─────────────────────────────────────────
    'bedrock.region': stringKey('eu-central-1', {
        description: 'AWS region for Bedrock (e.g., us-east-1, eu-central-1).',
        scope: 'workspace',
        category: 'Providers / AWS Bedrock',
    }),
    'bedrock.accessKeyId': stringKey('', {
        description: 'AWS access key ID for Bedrock.',
        scope: 'workspace',
        secret: true,
        category: 'Providers / AWS Bedrock',
    }),
    'bedrock.secretAccessKey': stringKey('', {
        description: 'AWS secret access key for Bedrock.',
        scope: 'workspace',
        secret: true,
        category: 'Providers / AWS Bedrock',
    }),
    'bedrock.modelId': stringKey('anthropic.claude-sonnet-4-6', {
        description: 'Bedrock model ID (e.g., anthropic.claude-sonnet-4-6).',
        scope: 'workspace',
        category: 'Providers / AWS Bedrock',
    }),
    'bedrock.additionalModels': stringArrayKey(['eu.anthropic.claude-haiku-4-5-20251001-v1:0'], {
        description: 'Additional Bedrock model IDs available in the model selector.',
        scope: 'workspace',
        category: 'Providers / AWS Bedrock',
    }),
    // ───── LLM / context windows ──────────────────────────────────────────
    // Map of model-id → context-window (tokens). Used by the SPA composer
    // to render a "ctx: X%" indicator scoped to the active session's model
    // (looked up by `SessionMeta.providerName` — Azure deployment name OR
    // Bedrock model id). When a model is missing from the map the SPA
    // falls back to a conservative 128k assumption. Values seeded here are
    // best-known defaults at schema-author time and may need correction
    // against vendor docs; users override per workspace or user scope.
    //
    // Verify each value against the vendor's published context-window
    // before relying on the % display for capacity planning.
    'llm.contextWindows': recordOfNumberKey({
        // Azure OpenAI deployments — assuming deployment names match the
        // underlying model SKU. GPT-5 family ships at 400k tokens per
        // OpenAI's public spec.
        'gpt-5.5': 400_000,
        'gpt-5.4': 400_000,
        'gpt-5.4-mini': 400_000,
        // Bedrock model IDs — Claude 4.x family runs at 200k tokens.
        'anthropic.claude-sonnet-4-6': 200_000,
        'anthropic.claude-haiku-4-5-20251001': 200_000,
        'eu.anthropic.claude-haiku-4-5-20251001-v1:0': 200_000,
    }, {
        description: 'Per-model context window in tokens. Keyed by model id (Azure deployment name or Bedrock model id). Drives the composer "ctx: X%" indicator.',
        scope: 'both',
        category: 'Providers',
    }),
    // ───── Proxy ───────────────────────────────────────────────────────────
    'proxy.url': stringKey('', {
        description: 'HTTP proxy URL for LLM API calls (e.g., http://proxy.corp.com:8080). May contain embedded credentials — redacted before API exposure.',
        scope: 'user',
        secret: true,
        category: 'Network / Proxy',
    }),
    'proxy.connectionTimeout': numberKey(30000, {
        description: 'Connection timeout in milliseconds for API calls (NFR27).',
        scope: 'user',
        category: 'Network / Proxy',
    }),
    'proxy.requestTimeout': numberKey(60000, {
        description: 'Request timeout in milliseconds for API calls (NFR27).',
        scope: 'user',
        category: 'Network / Proxy',
    }),
    // ───── Telemetry ───────────────────────────────────────────────────────
    'telemetry.optOut': booleanKey(false, {
        description: 'Opt out of telemetry data collection.',
        scope: 'user',
        category: 'Privacy & Compliance',
    }),
    // ───── Compliance / ITAR ──────────────────────────────────────────────
    // IV2-S5: master switch for data-residency enforcement. When false the
    // resolved `DataResidencyConfig.enabled` is false and the validator is a
    // no-op (Story 17.1 AC #3), so the Azure text provider behaves exactly as
    // it does today. When true, `resolveActiveProviderConfig` attaches a
    // `residencyConfig` to the active TessaConfig and the provider refuses a
    // residency-violating endpoint fail-closed at construction time.
    'compliance.dataResidencyEnabled': booleanKey(false, {
        description: 'Enable data residency enforcement (ITAR). When on, LLM API endpoints must match the configured region and allowed endpoint patterns or the provider refuses to transmit.',
        scope: 'user',
        category: 'Privacy & Compliance',
    }),
    'compliance.dataResidencyRegion': stringKey('', {
        description: 'Azure region for data residency enforcement (ITAR). When set, LLM API calls are restricted to endpoints in this region.',
        markdownDescription: 'Azure region for data residency enforcement (ITAR). Example: `usgovvirginia`.',
        scope: 'user',
        category: 'Privacy & Compliance',
    }),
    'compliance.allowedEndpointPatterns': stringArrayKey([], {
        description: 'Allowed Azure endpoint URL patterns for data residency enforcement (glob).',
        markdownDescription: 'Glob-style patterns (e.g., `*.openai.azure.us`). When non-empty, endpoints must match at least one pattern.',
        scope: 'user',
        category: 'Privacy & Compliance',
    }),
    'compliance.itarMode': booleanKey(false, {
        description: 'Enable ITAR compliance mode (status-bar indicator + endpoint validation).',
        scope: 'user',
        category: 'Privacy & Compliance',
    }),
    // ───── Export classification ───────────────────────────────────────────
    'exportClassification.enabled': booleanKey(false, {
        description: 'Enable export classification tagging on generated SysML files, CSV exports, and debug log exports.',
        scope: 'both',
        category: 'Privacy & Compliance / Export',
    }),
    'exportClassification.level': stringKey('', {
        description: "Classification level label (e.g., 'ITAR - USML Category XV').",
        scope: 'both',
        category: 'Privacy & Compliance / Export',
    }),
    'exportClassification.header': stringKey('', {
        description: 'Classification header text prepended to exported files.',
        scope: 'both',
        category: 'Privacy & Compliance / Export',
    }),
    'exportClassification.footer': stringKey('', {
        description: 'Classification footer text appended to exported files.',
        scope: 'both',
        category: 'Privacy & Compliance / Export',
    }),
    // ───── Collaboration ──────────────────────────────────────────────────
    'collaboration.serverUrl': stringKey('', {
        description: 'WebSocket URL for the TESSA Lean collaboration server. Empty disables real-time collaborative editing.',
        scope: 'user',
        category: 'Collaboration',
    }),
    'collaboration.displayName': stringKey('', {
        description: 'Your display name shown to collaborators during real-time editing sessions.',
        scope: 'user',
        category: 'Collaboration',
    }),
    // ───── Semantic pipeline ──────────────────────────────────────────────
    'semanticPipeline.maxConcurrentSyntaxCalls': numberKey(5, {
        description: 'Maximum number of concurrent syntax-agent LLM calls during semantic decomposition.',
        scope: 'both',
        category: 'Pipeline / Semantic',
    }),
    'semanticPipeline.maxConcurrentValidations': numberKey(3, {
        description: 'Maximum number of concurrent SysIDE validation calls during semantic decomposition.',
        scope: 'both',
        category: 'Pipeline / Semantic',
    }),
    'semanticPipeline.syntaxCallTimeoutMs': numberKey(180000, {
        description: 'Timeout in milliseconds for each syntax-agent LLM call (3-minute floor for whole-file rewrites).',
        scope: 'both',
        category: 'Pipeline / Semantic',
    }),
    'semanticPipeline.groupingThreshold': numberKey(7, {
        description: 'Maximum number of semantic changes per group before splitting into sub-groups.',
        scope: 'both',
        category: 'Pipeline / Semantic',
    }),
    'semanticPipeline.enableProgressiveRendering': booleanKey(true, {
        description: 'Enable progressive rendering of semantic changes as they complete.',
        scope: 'both',
        category: 'Pipeline / Semantic',
    }),
    'semanticPipeline.enablePreliminaryTierClassification': booleanKey(true, {
        description: 'Enable preliminary tier classification before full decomposition.',
        scope: 'both',
        category: 'Pipeline / Semantic',
    }),
    'semanticPipeline.enableParallelGrouping': booleanKey(true, {
        description: 'Enable parallel processing of independent semantic change groups.',
        scope: 'both',
        category: 'Pipeline / Semantic',
    }),
    'semanticPipeline.maxHierarchyDepth': numberKey(3, {
        description: 'Maximum depth of the semantic change hierarchy tree.',
        scope: 'both',
        category: 'Pipeline / Semantic',
    }),
    'semanticPipeline.domainProfile': stringKey('aerospace-do178c', {
        description: 'Domain profile to use for ontology-level decomposition.',
        scope: 'both',
        category: 'Pipeline / Semantic',
    }),
    'semanticPipeline.enableDecompositionCache': booleanKey(true, {
        description: 'Enable caching of decomposition results for repeated similar requests.',
        scope: 'both',
        category: 'Pipeline / Semantic',
    }),
    'semanticPipeline.enableTwoPhaseReview': booleanKey(false, {
        description: 'Enable two-phase review process for semantic changes.',
        scope: 'both',
        category: 'Pipeline / Semantic',
    }),
    'semanticPipeline.ontologyTemperature': numberKey(0, {
        description: 'LLM temperature for ontology-level decomposition. 0 = deterministic.',
        scope: 'both',
        category: 'Pipeline / Semantic',
    }),
    // ───── Tools ───────────────────────────────────────────────────────────
    'tools.autoApproveAll': booleanKey(false, {
        description: 'Unsafe mode. Auto-approves tool execution and skips confirmation dialogs.',
        markdownDescription: 'Unsafe mode. When enabled, TESSA auto-approves tool execution and skips confirmation dialogs where supported (including write/destructive tool paths).',
        scope: 'both',
        category: 'Tools',
    }),
    // ───── Git ─────────────────────────────────────────────────────────────
    'git.commitStrategy': enumKey(['per-change', 'per-changeset', 'manual'], 'per-change', {
        description: 'Git auto-commit strategy: per-change, per-changeset, or manual.',
        scope: 'workspace',
        category: 'Workspace / Git',
    }),
    'git.autoCommitEnabled': booleanKey(true, {
        description: 'Master switch for git auto-commit on change accept.',
        scope: 'workspace',
        category: 'Workspace / Git',
    }),
    // ───── Views ──────────────────────────────────────────────────────────
    // IV1-S3: default sourced from the single base-owned const. Previously the
    // dead literal 'output/views' — it matched NO runtime consumer (cli.ts
    // never read this key; createServer.opts.viewsOutputPath was always
    // undefined). The de-facto runtime default has always been
    // DEFAULT_VIEWS_OUTPUT_PATH ('.tessa-lean/views').
    'views.outputPath': stringKey(DEFAULT_VIEWS_OUTPUT_PATH, {
        description: 'Workspace-relative directory where auto-generated SysML v2 views are written.',
        scope: 'workspace',
        category: 'Workspace / Views',
    }),
    'views.renderer': enumKey(['syside', 'custom'], 'syside', {
        description: "Renderer for SysML v2 response views. 'syside' (default) uses the syside viz pipeline; 'custom' uses the in-webview Cytoscape.js renderer.",
        scope: 'both',
        category: 'Workspace / Views',
    }),
    'views.customLayout': enumKey(['elk-layered', 'elk-force', 'manual'], 'elk-layered', {
        description: "Default auto-layout algorithm for the custom renderer. 'manual' skips auto-layout and trusts layout.json.",
        scope: 'both',
        category: 'Workspace / Views',
    }),
    'views.skipProposal': booleanKey(false, {
        description: "When using the 'custom' renderer, skip the Keep/Discard proposal card and open the view directly.",
        scope: 'both',
        category: 'Workspace / Views',
    }),
    'views.autoGenerate': enumKey(['always', 'multi-element', 'off'], 'multi-element', {
        description: 'Controls when TESSA automatically produces a SysML v2 view alongside an answer.',
        scope: 'both',
        category: 'Workspace / Views',
    }),
    // ───── Workspace / Visualization (IV1-S7) ──────────────────────────────
    // Substrate keys for the LLM image visualization feature. Master toggle
    // defaults FALSE — when off the view path is byte-identical to the
    // deterministic renderer. Engine-selection runtime logic + the precedence
    // rule (enabled=false ⇒ deterministic) live at the read site in IV2-S2,
    // NOT here; IV1-S7 only declares the keys (and admin-gates image.*).
    'views.llmImage.enabled': booleanKey(false, {
        description: 'Master toggle for LLM image visualization. OFF by default — when false the view path is byte-identical to the deterministic renderer (the always-on React Flow sketch leads alone, with no "Generate LLM visualization" button, no Sketch ⇄ LLM-image engine switcher, and no adaptive raster escalation). When ON, the sketch still renders first; the raster is an opt-in follow-up fired only on explicit user action (or an expressiveness-gap / frustration escalation), never auto-fired.',
        scope: 'workspace',
        category: 'Workspace / Visualization',
    }),
    'views.llmImage.fallbackOnError': booleanKey(true, {
        description: 'When LLM image generation fails, silently fall back to the deterministic view representation.',
        scope: 'workspace',
        category: 'Workspace / Visualization',
    }),
    // RR-6/CDT-6 — 'diagram-as-code' removed from the SELECTABLE enum. The
    // engine MODULE is kept for future wiring, but the value silently no-ops to
    // deterministic (no render path is wired), so exposing it as a settable
    // option mis-led users. Only the two live engines are selectable.
    'views.engine': enumKey(['deterministic', 'llm-image'], 'deterministic', {
        description: "Default view rendering engine. 'deterministic' (default) uses the faithful KB-grounded React Flow sketch; 'llm-image' leads with a raster. Ignored unless views.llmImage.enabled is true. This is only the workspace default — each view carries a sticky per-view leadEngine (manifest.leadEngine, toggled by the card's engine switcher or an auto-escalation) that overrides this default for that view; clicking \"Back to sketch\" resets the view's lead to the deterministic sketch.",
        scope: 'workspace',
        category: 'Workspace / Visualization',
    }),
    'views.llmImage.stylePrompt': stringKey('', {
        description: 'Extra diagram-style notes APPENDED to the built-in modern default style (which always applies). Refines the look — e.g. "muted palette", "use a teal accent for the energy subsystem" — it adds to, and cannot remove, the default style or its guards. Leave blank to use the modern default as-is.',
        scope: 'workspace',
        category: 'Workspace / Visualization',
        multiline: true,
    }),
    // MAJ-9 — admin-settable hard upper bound on `nodes + edges` per raster
    // scene. The NodeAutoViewWriter skips the raster (falls back to the
    // deterministic view) above this ceiling; previously it was env-only
    // (TESSA_VIEWS_LLM_IMAGE_CEILING) so an admin could not raise/lower it from
    // the settings UI. Default 80 matches the writer's resolve-site fallback —
    // chosen so typical multi-subsystem KB views (the reference debug-workspace
    // IBD/tree views are ~46 nodes+edges) actually render rather than being
    // skipped as "too large"; the SysML source-of-truth + illustrative badge
    // cover the density/garble trade-off. Admins can lower it for stricter
    // fidelity or raise it for larger views.
    'views.llmImage.nodeEdgeCeiling': numberKey(80, {
        description: 'Hard upper bound on nodes + edges per LLM-image scene. Above this the raster is skipped and the deterministic view is used.',
        scope: 'workspace',
        category: 'Workspace / Visualization',
    }),
    // ───── Providers / Image (IV1-S7) ──────────────────────────────────────
    // Image-generation provider credentials/config. Like the Azure / Bedrock
    // provider groups these are workspace-scoped + admin-managed; the server
    // route admin-gates writes via the 'tessa.image.' prefix in
    // PROVIDER_KEY_PREFIXES and redacts image.apiKey (secret:true) on GET.
    'image.endpoint': stringKey('', {
        description: 'Azure image-generation endpoint URL (e.g. https://myinstance.cognitiveservices.azure.com).',
        scope: 'workspace',
        category: 'Providers / Image',
    }),
    'image.apiKey': stringKey('', {
        description: 'Image-generation API key.',
        scope: 'workspace',
        secret: true,
        category: 'Providers / Image',
    }),
    'image.deploymentName': stringKey('', {
        description: 'Image-generation deployment name (e.g. gpt-image-2).',
        scope: 'workspace',
        category: 'Providers / Image',
    }),
    'image.apiVersion': stringKey('2024-02-01', {
        description: 'Image-generation API version.',
        scope: 'workspace',
        category: 'Providers / Image',
    }),
    'image.quality': enumKey(['low', 'medium', 'high', 'auto'], 'medium', {
        description: "Image render quality. Default 'medium' — the IV1-S1 spike showed 'high' takes ~124s. Use 'high' only on explicit request.",
        scope: 'workspace',
        category: 'Providers / Image',
    }),
    // PC-2 — canvas size for the raster (also a pricing key). Previously
    // env-only (AZURE_IMAGE_SIZE) at the writer resolve site; declaring it here
    // makes it settable from the settings UI. gpt-image-2 supports a square +
    // two portrait/landscape ratios.
    'image.size': enumKey(['1024x1024', '1024x1536', '1536x1024'], '1024x1024', {
        description: 'Canvas size for generated raster images. Square (1024x1024), portrait (1024x1536), or landscape (1536x1024).',
        scope: 'workspace',
        category: 'Providers / Image',
    }),
    // ───── Prompt assembly ────────────────────────────────────────────────
    'prompt.stablePrefixAssembly': booleanKey(true, {
        description: 'Assemble the system prompt as a stable static prefix + volatile suffix to enable provider prefix caching.',
        scope: 'both',
        category: 'Pipeline / Prompt',
    }),
    // ───── Pre-connect / latency ─────────────────────────────────────────
    'preconnect.enabled': booleanKey(true, {
        description: 'Fire a HEAD request to the LLM endpoint at activation to warm TLS+DNS.',
        scope: 'both',
        category: 'Pipeline / Latency',
    }),
    // ───── Tool-result cap ────────────────────────────────────────────────
    'toolResultCap.enabled': booleanKey(true, {
        description: 'Persist oversized tool results to disk and replace inline payload with a pointer.',
        scope: 'both',
        category: 'Pipeline / Tool Results',
    }),
    // ───── Death-spiral guards ────────────────────────────────────────────
    'deathSpiralGuards.enabled': booleanKey(true, {
        description: 'Enable death-spiral circuit breakers on retry paths (AutoCompact, provider retry, stop-hook).',
        scope: 'both',
        category: 'Pipeline / Reliability',
    }),
    // ───── Memory (preview) ───────────────────────────────────────────────
    'memory.enabled': booleanKey(false, {
        description: 'Loads .tessa-lean/memory/MEMORY.md into each turn and enables /remember and /forget. Preview.',
        scope: 'both',
        category: 'Memory (preview)',
    }),
    // ───── Server / web client (W6-S1 NEW from PRD §8.1) ──────────────────
    'client.mode': enumKey(['webview', 'legacy-lit'], 'webview', {
        description: "Client mode: 'webview' (modern web SPA inside VS Code webview) or 'legacy-lit' (one-release fallback).",
        scope: 'user',
        category: 'Client',
    }),
    /** @reservedForFuture no runtime consumer as of 2026-05-13 — server listens on the `--port` CLI flag or a discovered free port; see post-merge-polish-followups.md MED-5. */
    'server.port': numberKey(7878, {
        description: 'TCP port the headless TESSA server listens on (loopback by default).',
        scope: 'user',
        category: 'Server',
    }),
    /** @reservedForFuture no runtime consumer as of 2026-05-13 — server bind interface is currently fixed; see post-merge-polish-followups.md MED-5. */
    'server.allowExternal': booleanKey(false, {
        description: 'Allow non-loopback connections to the headless server. Off by default for security.',
        scope: 'user',
        category: 'Server',
    }),
    // ───── Applier (KB-to-SysML) ──────────────────────────────────────────
    /** @reservedForFuture no runtime consumer as of 2026-05-13 — applier reads DEFAULT_BATCH_WINDOW_MS constant, no setting-to-deps wiring at the `createChangesetService` call site; see post-merge-polish-followups.md MED-5. */
    'applier.batchWindowMs': numberKey(500, {
        description: 'Debounce window in ms for batched .sysml file writes after entity acceptance.',
        scope: 'both',
        category: 'Pipeline / Applier',
    }),
    /** @reservedForFuture no runtime consumer as of 2026-05-13 — applier reads DEFAULT_MAX_PARALLEL constant, no setting-to-deps wiring at the `createChangesetService` call site; see post-merge-polish-followups.md MED-5. */
    'applier.maxParallel': numberKey(2, {
        description: 'Cross-changeset cap on parallel applier workers (Story D5 default = 2).',
        scope: 'both',
        category: 'Pipeline / Applier',
    }),
    // ───── File tree ───────────────────────────────────────────────────────
    /** @reservedForFuture no runtime consumer as of 2026-05-13 — file watcher uses hard-coded globs; see post-merge-polish-followups.md MED-5. */
    'fileTree.includeGlobs': stringArrayKey(['**/*.sysml', '**/tessa-config.json'], {
        description: 'Glob patterns the file tree watcher includes.',
        scope: 'workspace',
        category: 'Workspace / Files',
    }),
    /** @reservedForFuture no runtime consumer as of 2026-05-13 — chokidar polling fallback is not yet wired to this setting; see post-merge-polish-followups.md MED-5. */
    'fileTree.usePolling': booleanKey(false, {
        description: 'Force polling-based file watching instead of native fs events (network drives / WSL).',
        scope: 'workspace',
        category: 'Workspace / Files',
    }),
    // ───── KB index ────────────────────────────────────────────────────────
    /** @reservedForFuture no runtime consumer as of 2026-05-13 — KB indexer uses a fixed worker pool; see post-merge-polish-followups.md MED-5. */
    'kbIndex.parallelism': numberKey(4, {
        description: 'Number of parallel workers used by the KB indexer.',
        scope: 'both',
        category: 'Pipeline / Indexing',
    }),
    // ───── SysIDE LSP ──────────────────────────────────────────────────────
    /** @reservedForFuture no runtime consumer as of 2026-05-13 — syside discovery uses TESSA_SYSIDE_BIN env var, not this setting; see post-merge-polish-followups.md MED-5. */
    'syside.binPath': {
        schema: Type.Union([Type.String(), Type.Null()], { default: null }),
        meta: {
            default: null,
            description: 'Path to the SysIDE LSP binary. null lets the server discover via PATH.',
            scope: 'user',
            category: 'Workspace / SysIDE',
        },
    },
    /** @reservedForFuture no runtime consumer as of 2026-05-13 — syside pool sizing is not parameterised from settings; see post-merge-polish-followups.md MED-5. */
    'syside.poolSize': numberKey(2, {
        description: 'Number of SysIDE LSP processes kept warm in the pool.',
        scope: 'both',
        category: 'Workspace / SysIDE',
    }),
    /** @reservedForFuture no runtime consumer as of 2026-05-13 — syside idle reap is not parameterised from settings; see post-merge-polish-followups.md MED-5. */
    'syside.idleReapMs': numberKey(60000, {
        description: 'Idle ms before an extra SysIDE LSP process is reaped.',
        scope: 'both',
        category: 'Workspace / SysIDE',
    }),
    // ───── UI ──────────────────────────────────────────────────────────────
    /** @reservedForFuture no runtime consumer as of 2026-05-13 — SPA theme is inferred from VS Code/browser CSS variables; see post-merge-polish-followups.md MED-5. */
    'ui.theme': enumKey(['auto', 'light', 'dark'], 'auto', {
        description: 'UI theme. auto follows the host VS Code / browser preference.',
        scope: 'both',
        category: 'UI / Theme',
    }),
    /** @reservedForFuture no runtime consumer of the merged-settings store as of 2026-05-13 — SPA reads a parallel `tessa-lean.ui.utilityDrawer.autoOpenOnError` localStorage key in UtilityDrawer.tsx; see post-merge-polish-followups.md MED-5. */
    'ui.utilityDrawer.autoOpenOnError': booleanKey(true, {
        description: 'Auto-open the utility drawer when the assistant raises an error event.',
        scope: 'both',
        category: 'UI',
    }),
    /** @reservedForFuture no runtime consumer of the merged-settings store as of 2026-05-13 — SPA reads a parallel `tessa-lean.ui.showDebugTab` localStorage key in UtilityDrawer.tsx; see post-merge-polish-followups.md MED-5. */
    'ui.showDebugTab': booleanKey(false, {
        description: 'Show the debug tab in the SPA. Default off.',
        scope: 'both',
        category: 'UI',
    }),
    /** @reservedForFuture no runtime consumer as of 2026-05-13 — connectivity banner visibility is currently unconditional; see post-merge-polish-followups.md MED-5. */
    'ui.banner.hideWhenHealthy': booleanKey(false, {
        description: 'Hide the connectivity banner when all subsystems are healthy.',
        scope: 'both',
        category: 'UI',
    }),
    /** @reservedForFuture no runtime consumer as of 2026-05-13 — embed-vs-standalone banner gating is not wired to this setting; see post-merge-polish-followups.md MED-5. */
    'ui.banner.hideInEmbed': booleanKey(true, {
        description: 'Hide the connectivity banner when the SPA is embedded in VS Code.',
        scope: 'both',
        category: 'UI',
    }),
    /** @reservedForFuture no runtime consumer as of 2026-05-13 — status-bar item is unconditionally registered in extension.ts; see post-merge-polish-followups.md MED-5. */
    'ui.statusBar': booleanKey(true, {
        description: 'Show the TESSA status bar item in the host (extension only — ignored by the standalone web client).',
        scope: 'both',
        category: 'UI',
    }),
};
/** All settings keys as a frozen array — order is alphabetised on demand. */
export const SETTINGS_KEYS = Object.freeze(Object.keys(KEY_TABLE));
/**
 * Keys declared in the schema today but with NO runtime consumer of the
 * merged-settings store as of 2026-05-13. They still deserialize and
 * default-snapshot like any other key — the array exists so:
 *
 *   1. The schema-as-API-contract is honest about which keys are "wired"
 *      vs "reserved for future wiring".
 *   2. The SPA settings panel (W6-S2) can opt to dim or annotate these
 *      rows. (At time of tagging, no dim is applied — purely a doc
 *      contract; flip a row via `isReservedForFutureKey()` when ready.)
 *   3. Tooling (lint rules, doc generators) can cross-reference the
 *      `@reservedForFuture` JSDoc tag on each entry against this array.
 *
 * Track the broader follow-up in
 * `_bmad-output/implementation-artifacts/post-merge-polish-followups.md`
 * (MED-5). When a key lands a consumer:
 *   - Remove the `@reservedForFuture` JSDoc tag at the declaration site.
 *   - Remove the key from this array.
 *   - Let the snapshot test in `schema.test.ts` catch any drift.
 *
 * NOTE on the `ui.utilityDrawer.autoOpenOnError` / `ui.showDebugTab`
 * pair: the SPA reads a parallel `tessa-lean.<key>` localStorage value
 * in `packages/web-ui/src/AppShell/UtilityDrawer.tsx`. The merged-settings
 * path is still dead, so they remain in this list until the SPA migrates
 * to read them via `GET /api/v1/settings`.
 */
export const RESERVED_FOR_FUTURE_SETTINGS_KEYS = Object.freeze([
    'server.port',
    'server.allowExternal',
    'applier.batchWindowMs',
    'applier.maxParallel',
    'fileTree.includeGlobs',
    'fileTree.usePolling',
    'kbIndex.parallelism',
    'syside.binPath',
    'syside.poolSize',
    'syside.idleReapMs',
    'ui.theme',
    'ui.utilityDrawer.autoOpenOnError',
    'ui.showDebugTab',
    'ui.banner.hideWhenHealthy',
    'ui.banner.hideInEmbed',
    'ui.statusBar',
]);
/** O(1) lookup view over {@link RESERVED_FOR_FUTURE_SETTINGS_KEYS}. */
const RESERVED_FOR_FUTURE_SETTINGS_KEY_SET = new Set(RESERVED_FOR_FUTURE_SETTINGS_KEYS);
/**
 * True when the key is declared in the schema but has no runtime
 * consumer of the merged-settings store. See
 * {@link RESERVED_FOR_FUTURE_SETTINGS_KEYS}.
 */
export function isReservedForFutureKey(key) {
    return RESERVED_FOR_FUTURE_SETTINGS_KEY_SET.has(key);
}
/** The TypeBox schema for the entire merged-settings object (un-prefixed keys). */
export const SettingsSchema = Type.Object(Object.fromEntries(Object.entries(KEY_TABLE).map(([k, v]) => [k, v.schema])), { $id: 'TessaLeanSettings' });
/** Per-key meta sidecar (description, scope, secret, category). */
export const SETTINGS_META = Object.freeze(Object.fromEntries(Object.entries(KEY_TABLE).map(([k, v]) => [k, v.meta])));
/** Map a bare key (`azure.endpoint`) to its on-disk JSON form (`tessa.azure.endpoint`). */
export function qualifiedKey(key) {
    return `${SETTINGS_PREFIX}.${key}`;
}
/** Inverse of `qualifiedKey` — returns `null` if the prefix doesn't match. */
export function unqualifyKey(qualified) {
    const prefix = `${SETTINGS_PREFIX}.`;
    if (!qualified.startsWith(prefix))
        return null;
    const k = qualified.slice(prefix.length);
    return (k in KEY_TABLE ? k : null);
}
/**
 * Return the schema's per-key default — used by the merged-settings
 * reader's fallback path so callers always receive a value.
 */
export function defaultFor(key) {
    return SETTINGS_META[key].default;
}
/** Snapshot helper — returns a fresh object filled with all defaults. */
export function defaultsSnapshot() {
    const out = {};
    for (const key of SETTINGS_KEYS) {
        out[key] = SETTINGS_META[key].default;
    }
    return out;
}
/** True when the key is flagged secret in the meta sidecar. */
export function isSecretKey(key) {
    return SETTINGS_META[key].secret === true;
}
/** Categorised view — used by the SPA settings panel left rail. */
export function settingsByCategory() {
    const out = new Map();
    for (const key of SETTINGS_KEYS) {
        const cat = SETTINGS_META[key].category;
        const list = out.get(cat) ?? [];
        list.push(key);
        out.set(cat, list);
    }
    return out;
}
//# sourceMappingURL=schema.js.map